import { z } from "zod";
import { StableUuidSchema } from "./identifiers.js";
import { CalendarMonthSchema } from "./time.js";
export const PayrollOvertimeReportRangeSchema = z
    .object({ fromMonth: CalendarMonthSchema, throughMonth: CalendarMonthSchema })
    .strict()
    .refine((value) => value.fromMonth <= value.throughMonth, {
    path: ["throughMonth"],
    message: "From month must not be after through month.",
})
    .refine((value) => {
    const index = (month) => Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7));
    return index(value.throughMonth) - index(value.fromMonth) < 120;
}, {
    path: ["throughMonth"],
    message: "Choose at most 120 months for the overtime report.",
});
export const ContractualRateSchema = z
    .string()
    .regex(/^(?:0|[1-9]\d{0,9})(?:\.\d{1,2})?$/, "Enter a positive EUR amount with at most two decimals.")
    .refine((value) => /[1-9]/.test(value), "Rate must be greater than zero.");
export const PAYROLL_INTERNAL_ACTOR_REVISION = 1;
// OpenProject's Users API activation states; null means no usable observation.
export const OpenProjectAccountStatusSchema = z.enum([
    "active",
    "registered",
    "locked",
    "invited",
]);
export const PayrollEmployeeStatusSchema = z.enum([
    "active",
    "inactive",
    "on_leave",
    "terminated",
]);
export const PayrollEmployeeSchema = z
    .object({
    id: StableUuidSchema,
    humanPrincipalId: StableUuidSchema.nullable(),
    openProjectSourceUserId: z.string().min(1).max(128).nullable(),
    sourceAccountStatus: OpenProjectAccountStatusSchema.nullable().default(null),
    payrollEmployeeCode: z.string().max(64),
    status: PayrollEmployeeStatusSchema,
    username: z.string().min(1).max(200),
    displayName: z.string().min(1).max(200),
    email: z.string().email().max(320).nullable(),
    revision: z.number().int().nonnegative(),
})
    .strict();
export const PayrollInternalActorClaimsSchema = z
    .object({
    actorRevision: z.literal(PAYROLL_INTERNAL_ACTOR_REVISION),
    actorType: z.literal("human"),
    sub: StableUuidSchema,
    sid: StableUuidSchema,
    jti: StableUuidSchema,
    authorizationRevision: z.number().int().nonnegative(),
})
    .strict();
export const PayrollInternalProjectionClaimsSchema = z
    .object({
    actorRevision: z.literal(PAYROLL_INTERNAL_ACTOR_REVISION),
    actorType: z.literal("service"),
    sub: z.literal("openproject-sync"),
    service: z.literal("openproject-sync"),
    jti: StableUuidSchema,
})
    .strict();
export const OpenProjectEmployeeProjectionUserSchema = z
    .object({
    openProjectUserId: z.number().int().positive().safe(),
    username: z.string().trim().min(1).max(200),
    email: z.string().trim().max(320),
    displayName: z.string().trim().min(1).max(200),
    active: z.boolean(),
})
    .strict();
export const OpenProjectEmployeeProjectionSchema = z
    .object({
    // Keeps even heavily escaped JSON below the payroll API's 25 MiB default.
    users: z.array(OpenProjectEmployeeProjectionUserSchema).max(5_000),
    // Directory metadata is not an enrollment list. Older callers may omit it.
    sourceAccountStatuses: z
        .array(z
        .object({
        openProjectUserId: z.number().int().positive().safe(),
        status: OpenProjectAccountStatusSchema.nullable(),
    })
        .strict())
        .max(50_000)
        .optional(),
})
    .strict();
export const OpenProjectEmployeeProjectionResultSchema = z
    .object({
    projected: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    deactivated: z.number().int().nonnegative(),
})
    .strict();
export const PayrollMutationMetadataSchema = z
    .object({
    expectedAuthorizationRevision: z.number().int().nonnegative(),
    expectedRevision: z.number().int().nonnegative(),
    reason: z.string().trim().min(8).max(500),
    idempotencyKey: z
        .string()
        .min(16)
        .max(128)
        .regex(/^[A-Za-z0-9._:-]+$/),
})
    .strict();
export const LockPayrollTimesheetSchema = PayrollMutationMetadataSchema.extend({
    employeeId: StableUuidSchema,
    month: CalendarMonthSchema,
    mirrorRunId: StableUuidSchema.optional(),
}).strict();
export const PayrollWorkProfileMutationSchema = PayrollMutationMetadataSchema.extend({
    employeeId: StableUuidSchema,
    payrollEmployeeCode: z.string().trim().max(64),
    payrollEmail: z.union([z.string().trim().email().max(320), z.literal("")]),
    payrollPdfPassword: z.string().min(8).max(127).optional(),
    calculationEnabled: z.boolean().optional(),
    contractType: z
        .enum(["employee", "student_agreement", "freelancer", "other"])
        .optional(),
    workloadPercent: z.number().positive().max(100).optional(),
    hoursPerDay: z.number().positive().max(24).optional(),
    annualHolidayEntitlement: z.number().nonnegative().max(366).optional(),
    startDate: z.string().max(10).optional(),
    endDate: z.string().max(10).optional(),
    notes: z.string().max(2_000),
}).strict();
export const PayrollWorkflowMutationSchema = PayrollMutationMetadataSchema.extend({
    month: CalendarMonthSchema,
    action: z.enum(["advance", "close", "reopen"]),
    fromStage: z
        .enum(["prepare", "pohoda", "payslips", "reconcile", "approve", "close"])
        .optional(),
})
    .strict()
    .refine((value) => value.action !== "reopen" || value.reason.length >= 15, {
    message: "Reopen reason must contain at least 15 characters.",
    path: ["reason"],
});
export const PayrollAbsenceCategorySchema = z.enum([
    "vacation",
    "doctor",
    "illness",
    "family-care",
    "paid-leave",
    "holiday",
]);
export const PayrollAbsenceMappingSchema = z
    .object({
    categoryById: z
        .record(z
        .string()
        .regex(/^[1-9]\d{0,15}$/)
        .refine((value) => Number.isSafeInteger(Number(value))), PayrollAbsenceCategorySchema)
        .refine((value) => Object.keys(value).length <= 1000, "Too many mapped work packages"),
    ordinaryWorkTypes: z.array(z.string().trim().min(1).max(100)).max(100),
})
    .strict();
export const PayrollAbsenceSettingsSchema = z
    .object({
    mapping: PayrollAbsenceMappingSchema,
    revision: z.number().int().nonnegative(),
    checksum: z.string().regex(/^(?:[a-f0-9]{64})?$/),
})
    .strict();
export const PayrollAbsenceSettingsMutationSchema = PayrollMutationMetadataSchema.extend({
    mapping: PayrollAbsenceMappingSchema,
}).strict();
export const PAYROLL_ABSENCE_CATEGORY_LABELS = {
    vacation: "Vacation",
    doctor: "Doctor",
    illness: "Illness",
    "family-care": "Family care",
    "paid-leave": "Paid leave",
    holiday: "Public holiday",
};
//# sourceMappingURL=payroll.js.map